import {useQuery} from '@tanstack/react-query';
import {AxiosResponse} from 'axios';
import {IApiDataResponse} from '../../dto/API.dto';
import {IStation} from '../../dto/Station.dto';
import {apiGet} from '../../axiosConfig';

export type IListQueryParams = {
  search?: string;
  page?: number;
  per_page: number;
  sort_field?: string;
  sort_order?: string;
  mirror?: string;
};

export type IStationWithParent = IStation & {
  parent_station: IStation | null;
  is_shared_facility?: boolean;
  password?: string;
};

/**
 * The Content Transfer Portal "From" side can be an archived partner, which the ordinary
 * company station route hides behind its route binding. The portal has its own, permission
 * scoped endpoint that resolves one; everything else must keep using the ordinary route.
 */
const stationsUrl = (company_id: number, includeArchivedPartner: boolean) =>
  includeArchivedPartner
    ? `/v1/global-controls/content-transfer/partner/${company_id}/stations`
    : `/v1/company/${company_id}/stations`;

const getStations = (
  company_id: number,
  mirror: string,
  odo: string,
  classRoom: boolean,
  isReplicate: boolean = false,
  includeArchivedPartner: boolean = false,
) => {
  return apiGet(stationsUrl(company_id, includeArchivedPartner), {
    params: {
      mirror,
      odo,
      classRoom: classRoom ? 1 : 0,
      isReplicate: isReplicate ? 1 : 0,
    },
  }).then((response: AxiosResponse<IApiDataResponse<IStationWithParent[]>>) => {
    return response.data.data;
  });
};

export const getStation = (
  company_id: number,
  mirror: string,
  odo: string,
  classRoom: boolean,
  stationId: number,
) => {
  return apiGet(`/v1/company/${company_id}/stations`, {
    params: {mirror, odo, classRoom: classRoom ? 1 : 0, stationId: stationId},
  }).then((response: AxiosResponse<IApiDataResponse<IStationWithParent[]>>) => {
    return response.data.data[0];
  });
};

export const useRqListCompanyStations = (
  company_id: number,
  enabled: boolean = true,
  mirror: string = '',
  odo: string = '',
  classRoom: boolean = true,
  isReplicate: boolean = false,
  includeArchivedPartner: boolean = false,
) => {
  return useQuery({
    initialData: [],
    queryKey: [
      'api/v1/company',
      company_id,
      'stations',
      mirror,
      odo,
      classRoom,
      // Part of the key because it selects a different endpoint.
      includeArchivedPartner,
    ],
    queryFn: () =>
      getStations(
        company_id,
        mirror,
        odo,
        classRoom,
        isReplicate,
        includeArchivedPartner,
      ),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled,
    select: stations => {
      const parentStationMap = new Map(stations.map(s => [s.id, s]));

      return stations.map(s => ({
        ...s,
        parent_station: parentStationMap.get(Number(s.parent_id)) || null,
      }));
    },
  });
};
/***
 *  Get Station Info
 */
export const getStationInfoPath = (company_id: number, station_id: number) =>
  `/v1/company/${String(company_id)}/station/${station_id}`;

const getStationInfo = (company_id: number, station_id: number) => {
  return apiGet(getStationInfoPath(company_id, station_id)).then(
    (response: AxiosResponse<IApiDataResponse<IStation>>) => {
      return response.data.data;
    },
  );
};

const getStationClips = (company_id: number, station_id: number) => {
  return apiGet(`/v1/company/${company_id}/stations/${station_id}/clip`).then(
    (response: AxiosResponse) => {
      return response.data;
    },
  );
};

export const useRqStationsClips = (
  company_id: number,
  station_id: number,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [`/v1/company/${company_id}/stations/${station_id}/clip`],
    queryFn: () => getStationClips(company_id, station_id),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled,
  });
};

export const RqStationInfo = (
  company_id: number,
  station_id: number,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [getStationInfoPath(company_id, station_id)],
    queryFn: () => getStationInfo(company_id, station_id),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
    retry: false,
    enabled: enabled,
  });
};
